import { browser } from "$app/environment"
import { localStorageStore } from "../local-storage-store"

/**
 * Copies over existing properties from the source object to the destination object.
 * Only properties that exist in both objects and have non-null and non-undefined values will be copied.
 *
 * @template T - The type of the destination object.
 * @template K - The keys of the destination object.
 * @param {T} destination - The destination object.
 * @param {{ [P in K]?: T[P] | null }} source - The source object.
 * @returns {void}
 */
export function copyOverExistingProperties<T extends object, K extends keyof T>(
  destination: T,
  source: { [P in K]?: T[P] | null }
) {
  for (const key in source) {
    const value = source[key]
    // for some reason, isNil(value) doesn't narrow the type to exclude null/undefined
    if (key in destination && value !== undefined && value !== null) {
      destination[key] = value
    }
  }
}

/**
 * Flatten a nested object by joining the keys with a dot.
 *
 * @example
 *   flattenObject{ a: 1, b: { c: 2 } }
 *   // { a: 1, b.c: 2}
 */
export function flattenObject(obj: object, keys: string[] = []) {
  return Object.entries(obj).reduce<Record<string, unknown>>(
    (acc, [key, value]) => {
      const updatedKeys = [...keys, key]
      if (
        typeof value === "object" &&
        value !== null &&
        !Array.isArray(value)
      ) {
        Object.assign(acc, flattenObject(value, updatedKeys))
      } else {
        acc[updatedKeys.join(".")] = value
      }
      return acc
    },
    {}
  )
}

export function delay(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export function getIsMobile() {
  return window?.screen?.width ? window.screen.width <= 720 : undefined
}

const WEBVIEW_PARAMS = ["ios", "android"] as const

export type WebViewPlatform = (typeof WEBVIEW_PARAMS)[number] | null

/** Persisted because the param rides only the initial document URL. */
export function getWebViewPlatform() {
  const store = localStorageStore<WebViewPlatform>("webview-platform", null)
  if (!browser) return store
  const searchParams = new URLSearchParams(window.location.search)
  const present = WEBVIEW_PARAMS.filter((platform) =>
    searchParams.has(platform)
  )
  if (present.length) {
    store.set(
      present.find((platform) => searchParams.get(platform) === "true") ?? null
    )
  }
  return store
}

/* v8 ignore start -- @preserve */
if (import.meta.vitest) {
  const { it, describe, expect, vi, beforeEach, afterEach } = import.meta.vitest

  describe("copyOverExistingProperties", () => {
    it("copies over existing properties", () => {
      const destination = { a: 0, b: 0, c: 0 }
      const source = { a: 1, b: 2 }
      copyOverExistingProperties(destination, source)
      expect(destination).toEqual({ a: 1, b: 2, c: 0 })
    })

    it("Does not copy properties that don't exist on destination", () => {
      const source = { a: 1, b: 2, d: 3 }
      const destination = { a: 0, b: 0, c: 0 }
      copyOverExistingProperties(destination, source)
      expect(destination).toEqual({ a: 1, b: 2, c: 0 })
    })
  })

  describe("flattenObject", () => {
    it("correctly flattens an object", () => {
      const input = {
        a: {
          b: "hello",
        },
        c: "hi",
        d: {
          e: {
            f: "hey",
          },
          g: "howdy",
        },
      }
      const output = { "a.b": "hello", c: "hi", "d.e.f": "hey", "d.g": "howdy" }

      expect(flattenObject(input)).toEqual(output)
    })
  })

  describe("delay", () => {
    it("correctly returns a promise that resolves correctly", () => {
      vi.useFakeTimers()

      vi.spyOn(globalThis, "setTimeout").mockImplementation((fn: () => any) =>
        fn()
      )

      delay(1)

      vi.runAllTimers()
      expect(global.setTimeout).toHaveBeenCalledOnce()
      vi.useRealTimers()
    })
  })

  describe("getIsMobile", () => {
    let originalWidth: PropertyDescriptor | undefined

    beforeEach(() => {
      originalWidth = Object.getOwnPropertyDescriptor(window.screen, "width")
    })

    afterEach(() => {
      if (originalWidth) {
        Object.defineProperty(window.screen, "width", originalWidth)
      }
    })

    function setScreenWidth(value: number) {
      Object.defineProperty(window.screen, "width", {
        configurable: true,
        value,
      })
    }

    it("returns true when screen width is below the mobile breakpoint", () => {
      setScreenWidth(375)
      expect(getIsMobile()).toBe(true)
    })

    it("returns true at the 720px breakpoint", () => {
      setScreenWidth(720)
      expect(getIsMobile()).toBe(true)
    })

    it("returns false at 721px", () => {
      setScreenWidth(721)
      expect(getIsMobile()).toBe(false)
    })

    it("returns false on desktop viewports", () => {
      setScreenWidth(1280)
      expect(getIsMobile()).toBe(false)
    })

    it("returns undefined when screen width is unavailable", () => {
      setScreenWidth(0)
      expect(getIsMobile()).toBeUndefined()
    })
  })
}
